import type { DragEvent } from 'react';
import type { ExerciseItem } from '../../../types/exercise';

const TRAINING_DRAG_DATA_KEY = 'application/x-portal-training-drag';

export type TrainingDragData =
  | {
      type: 'catalog-exercise';
      exercise: ExerciseItem;
    }
  | {
      type: 'block-exercise';
      blockId: string;
      exerciseId: string;
    };

let activeTrainingDrag: TrainingDragData | null = null;

export const getActiveTrainingDrag = () => activeTrainingDrag;

export const clearActiveTrainingDrag = () => {
  activeTrainingDrag = null;
};

export const setTrainingDragData = (
  event: DragEvent<HTMLElement>,
  payload: TrainingDragData
) => {
  activeTrainingDrag = payload;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData(TRAINING_DRAG_DATA_KEY, JSON.stringify(payload));
  event.dataTransfer.setData(
    'text/plain',
    payload.type === 'catalog-exercise' ? payload.exercise.name : payload.exerciseId
  );
};

export const setTrainingDragPreview = (
  event: DragEvent<HTMLElement>,
  previewSource: HTMLElement,
  options?: { onlyImage?: boolean }
) => {
  let previewElement: HTMLElement;
  let previewWidth = 1;
  let previewHeight = 1;
  let previewOffsetX = 0;
  let previewOffsetY = 0;

  if (options?.onlyImage) {
    // Try to find the first img inside previewSource
    const img = previewSource.querySelector('img');
    if (img) {
      previewElement = img.cloneNode(true) as HTMLElement;
      const rect = img.getBoundingClientRect();
      previewWidth = Math.max(Math.round(rect.width), 1);
      previewHeight = Math.max(Math.round(rect.height), 1);
      previewOffsetX = Math.min(Math.max(Math.round(event.clientX - rect.left), 0), previewWidth);
      previewOffsetY = Math.min(Math.max(Math.round(event.clientY - rect.top), 0), previewHeight);
      previewElement.style.position = 'fixed';
      previewElement.style.top = '-2000px';
      previewElement.style.left = '-2000px';
      previewElement.style.width = `${previewWidth}px`;
      previewElement.style.height = `${previewHeight}px`;
      previewElement.style.pointerEvents = 'none';
      previewElement.style.margin = '0';
      previewElement.style.opacity = '1';
      previewElement.style.zIndex = '9999';
      previewElement.setAttribute('aria-hidden', 'true');
      (previewElement as HTMLImageElement).draggable = false;
      document.body.appendChild(previewElement);
      event.dataTransfer.setDragImage(previewElement, previewOffsetX, previewOffsetY);
      window.setTimeout(() => {
        if (previewElement.parentNode) {
          previewElement.parentNode.removeChild(previewElement);
        }
      }, 0);
      return;
    }
  }

  // fallback: clone the full element
  const sourceRect = previewSource.getBoundingClientRect();
  previewElement = previewSource.cloneNode(true) as HTMLElement;
  previewWidth = Math.max(Math.round(sourceRect.width), 1);
  previewHeight = Math.max(Math.round(sourceRect.height), 1);
  previewOffsetX = Math.min(
    Math.max(Math.round(event.clientX - sourceRect.left), 0),
    previewWidth
  );
  previewOffsetY = Math.min(
    Math.max(Math.round(event.clientY - sourceRect.top), 0),
    previewHeight
  );
  previewElement.setAttribute('aria-hidden', 'true');
  previewElement.querySelectorAll('img').forEach((image) => {
    image.draggable = false;
  });
  previewElement.style.position = 'fixed';
  previewElement.style.top = '-2000px';
  previewElement.style.left = '-2000px';
  previewElement.style.width = `${previewWidth}px`;
  previewElement.style.height = `${previewHeight}px`;
  previewElement.style.maxWidth = 'none';
  previewElement.style.pointerEvents = 'none';
  previewElement.style.margin = '0';
  previewElement.style.opacity = '1';
  previewElement.style.transform = 'none';
  previewElement.style.zIndex = '9999';
  document.body.appendChild(previewElement);
  event.dataTransfer.setDragImage(
    previewElement,
    previewOffsetX,
    previewOffsetY
  );
  window.setTimeout(() => {
    if (previewElement.parentNode) {
      previewElement.parentNode.removeChild(previewElement);
    }
  }, 0);
};

export const EXERCISE_DROP_EDGE_RATIO = 0.2;
export const DROP_ZONE_POINTER_LOCK_PX = 18;
const REORDER_ZONE_HYSTERESIS = 0.2;

export type ExerciseDropZone = 'before' | 'after' | 'conjugate';

const getDropRatio = (
  clientX: number,
  targetRect: Pick<DOMRect, 'left' | 'width'>,
) => {
  if (targetRect.width <= 0) {
    return 0.5;
  }

  return (clientX - targetRect.left) / targetRect.width;
};

export const resolveExerciseDropZone = (
  clientX: number,
  targetRect: Pick<DOMRect, 'left' | 'width'>,
  edgeRatio = EXERCISE_DROP_EDGE_RATIO,
  currentZone?: ExerciseDropZone | null,
  pointerDeltaPx = Number.POSITIVE_INFINITY,
  lockPx = DROP_ZONE_POINTER_LOCK_PX,
): ExerciseDropZone => {
  if (targetRect.width <= 0) {
    return currentZone ?? 'conjugate';
  }

  const ratio = getDropRatio(clientX, targetRect);
  const nextZone = ratio <= edgeRatio
    ? 'before'
    : (ratio >= 1 - edgeRatio ? 'after' : 'conjugate');

  if (currentZone && pointerDeltaPx < lockPx) {
    return currentZone;
  }

  return nextZone;
};

export const resolveReorderDropZone = (
  clientX: number,
  targetRect: Pick<DOMRect, 'left' | 'width'>,
  currentZone?: Exclude<ExerciseDropZone, 'conjugate'> | null,
): Exclude<ExerciseDropZone, 'conjugate'> => {
  if (targetRect.width <= 0) {
    return currentZone ?? 'before';
  }

  const ratio = getDropRatio(clientX, targetRect);

  if (currentZone === 'before' && ratio < 0.5 + REORDER_ZONE_HYSTERESIS) {
    return 'before';
  }

  if (currentZone === 'after' && ratio > 0.5 - REORDER_ZONE_HYSTERESIS) {
    return 'after';
  }

  return ratio < 0.5 ? 'before' : 'after';
};

export const dropZoneToDropMode = (zone: ExerciseDropZone): 'insert' | 'insert-after' | 'conjugate' => {
  if (zone === 'before') {
    return 'insert';
  }

  if (zone === 'after') {
    return 'insert-after';
  }

  return 'conjugate';
};

export const isTrainingExerciseDragEvent = (
  event: Pick<DragEvent<HTMLElement>, 'dataTransfer'>,
) => Array.from(event.dataTransfer.types).includes(TRAINING_DRAG_DATA_KEY);

export const getTrainingDragData = (
  event: DragEvent<HTMLElement>
): TrainingDragData | null => {
  const rawPayload = event.dataTransfer.getData(TRAINING_DRAG_DATA_KEY);

  if (!rawPayload) {
    return null;
  }

  try {
    const parsedPayload = JSON.parse(rawPayload) as TrainingDragData;

    if (parsedPayload.type === 'catalog-exercise' && parsedPayload.exercise) {
      return parsedPayload;
    }

    if (
      parsedPayload.type === 'block-exercise' &&
      typeof parsedPayload.blockId === 'string' &&
      typeof parsedPayload.exerciseId === 'string'
    ) {
      return parsedPayload;
    }

    return null;
  } catch {
    return null;
  }
};